import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, PhoneCall, Loader2, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  queued: { label: 'En cola', cls: 'bg-yellow-500/20 text-yellow-400' },
  'in-progress': { label: 'En curso', cls: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completada', cls: 'bg-green-500/20 text-green-400' },
  failed: { label: 'Fallida', cls: 'bg-red-500/20 text-red-400' },
  'no-answer': { label: 'Sin respuesta', cls: 'bg-gray-500/20 text-gray-400' },
  busy: { label: 'Ocupado', cls: 'bg-orange-500/20 text-orange-400' },
  voicemail: { label: 'Buzón de voz', cls: 'bg-purple-500/20 text-purple-400' },
  canceled: { label: 'Cancelada', cls: 'bg-gray-500/20 text-gray-400' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '—';
  const totalSeconds = Math.round(Number(minutes) * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

export const AynaraCallsHub = ({ authFetch }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/bland/calls`);
      const data = await res.json();
      setCalls(data.calls || []);
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  return (
    <div className="space-y-4" data-testid="admin-aynara-calls">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-primary" /> Llamadas de Aynara (Bland.ai)
          </h3>
          <p className="text-sm text-text-muted">Historial de llamadas salientes solicitadas desde la web, con estado y duración.</p>
        </div>
        <button
          type="button"
          onClick={loadCalls}
          disabled={loading}
          className="btn-secondary !px-4 !py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="admin-aynara-calls-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualizar
        </button>
      </div>

      <div className="glass-card overflow-hidden" data-testid="admin-aynara-calls-table">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left p-4 text-text-muted font-medium">Fecha</th>
                <th className="text-left p-4 text-text-muted font-medium">Teléfono</th>
                <th className="text-left p-4 text-text-muted font-medium hidden md:table-cell">Nombre</th>
                <th className="text-left p-4 text-text-muted font-medium">Estado</th>
                <th className="text-left p-4 text-text-muted font-medium">Duración</th>
                <th className="text-left p-4 text-text-muted font-medium hidden lg:table-cell">Contestó</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((call, i) => {
                const status = STATUS_STYLE[call.status] || { label: call.status || '—', cls: 'bg-gray-500/20 text-gray-400' };
                const hasDetail = Boolean(call.summary || call.transcript);
                const isOpen = expanded === (call.id || i);
                return (
                  <React.Fragment key={call.id || i}>
                    <tr
                      className={`border-b border-border-subtle/50 hover:bg-card-hover transition-colors ${hasDetail ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetail && setExpanded(isOpen ? null : (call.id || i))}
                      data-testid={`admin-aynara-call-row-${i}`}
                    >
                      <td className="p-4 text-text-body whitespace-nowrap">{formatDate(call.created_at)}</td>
                      <td className="p-4 text-text-heading font-medium">{call.phone_number}</td>
                      <td className="p-4 text-text-body hidden md:table-cell">{call.visitor_name || '—'}</td>
                      <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${status.cls}`}>{status.label}</span></td>
                      <td className="p-4 text-text-body">{formatDuration(call.duration_minutes)}</td>
                      <td className="p-4 text-text-body hidden lg:table-cell">
                        <span className="inline-flex items-center gap-2">
                          {call.answered_by === 'human' ? 'Persona' : call.answered_by === 'voicemail' ? 'Buzón' : call.answered_by || '—'}
                          {hasDetail && (isOpen ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-primary" />)}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-border-subtle/50 bg-slate-950/40" data-testid={`admin-aynara-call-detail-${i}`}>
                        <td colSpan={6} className="p-4">
                          {call.summary && (
                            <div className="rounded-xl border border-primary/25 bg-primary/10 p-4 mb-3">
                              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Qué quiere el cliente</p>
                              <p className="text-sm text-text-body whitespace-pre-line">{call.summary}</p>
                            </div>
                          )}
                          {call.transcript && (
                            <details className="rounded-xl border border-border-subtle bg-slate-950/60 p-4">
                              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.18em] text-text-muted">Transcripción completa</summary>
                              <p className="mt-3 max-h-64 overflow-y-auto text-sm text-text-body whitespace-pre-line">{call.transcript}</p>
                            </details>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && calls.length === 0 && (
          <p className="p-8 text-center text-text-muted" data-testid="admin-aynara-calls-empty">Aún no hay llamadas registradas. Se crean desde el botón "Llamar ahora" de la Experiencia 3D.</p>
        )}
        {loading && calls.length === 0 && <p className="p-8 text-center text-text-muted">Cargando llamadas…</p>}
      </div>
    </div>
  );
};

export default AynaraCallsHub;
